/**
 * mapService.ts — Servizio centralizzato per tutte le funzionalità mappa
 * RideConnect | Stack: Leaflet + Stadia Maps (dark) + OSRM + Nominatim + Geoapify (fallback)
 *
 * MIGLIORAMENTI v2:
 *  1. Routing primario via OSRM (free, no key) + Geoapify come fallback
 *  2. Geocoding primario via Nominatim/OSM (free, no key) + Geoapify come fallback
 *  3. Debounce helper (400ms) per limitare le chiamate durante la digitazione
 *  4. Cache con TTL: percorsi 30min, geocoding 10min, reverse 15min
 *  5. Modalità "motorcycle" nel routing (profilo moto reale)
 *  6. Retry automatico con exponential backoff (max 2 retry)
 *
 * Provider tile: Stadia Maps "Alidade Smooth Dark" (grafica nera, no key)
 * Routing: OSRM public → Geoapify fallback (VITE_GEOAPIFY_KEY)
 * Geocoding: Nominatim/OSM → Geoapify fallback
 * Geolocalizzazione: Browser Geolocation API
 */

import { getGeoapifyKey, getGeoapifyMapStyle } from "@/utils/config";
import { buildGeoLabel } from "rideconnect-shared/geoLabel";

// ─── TIPI PUBBLICI ────────────────────────────────────────────────────────────

export interface Coordinates {
  lat: number;
  lng: number;
}

export interface RouteWaypoint extends Coordinates {
  label?: string;
}

export interface RouteResult {
  /** Array di coordinate che compongono il tracciato */
  coordinates: Coordinates[];
  /** Distanza totale in km */
  distanceKm: number;
  /** Durata stimata in minuti */
  durationMin: number;
  /** Profilo altimetrico in metri (disponibile solo con Geoapify) */
  elevationProfile?: number[];
  /** GeoJSON grezzo restituito dall'API */
  rawGeojson?: unknown;
  /** Provider effettivamente usato per il calcolo */
  provider?: "osrm" | "geoapify";
  /** Indice di tortuosità della strada (1-10). Calcolato lato client. */
  curveScore?: number;
}

export interface GeocodingResult {
  label: string;
  coordinates: Coordinates;
  /** Indirizzo strutturato (opzionale) */
  details?: {
    street?: string;
    city?: string;
    country?: string;
    postcode?: string;
  };
}

export type MarkerType = "origin" | "destination" | "stop" | "user";
export type TileTheme = "dark" | "light" | "satellite";
export type RoutingMode = "motorcycle" | "drive" | "walk" | "bicycle";

// ─── ERRORE PERSONALIZZATO ────────────────────────────────────────────────────

export class MapServiceError extends Error {
  constructor(
    message: string,
    public readonly code:
      | "ROUTE_FETCH_FAILED"
      | "GEOCODE_FAILED"
      | "REVERSE_GEOCODE_FAILED"
      | "GEOLOCATION_UNSUPPORTED"
      | "GEOLOCATION_DENIED"
      | "GEOLOCATION_TIMEOUT"
      | "GEOLOCATION_ERROR"
      | "MAP_INIT_FAILED"
      | "NO_API_KEY",
  ) {
    super(message);
    this.name = "MapServiceError";
  }
}

// ─── CACHE CON TTL ────────────────────────────────────────────────────────────
// TTL: percorsi 30min | geocoding 10min | reverse geocoding 15min

interface CacheEntry<T> {
  value: T;
  expiresAt: number;
}

function makeTTLCache<T>(ttlMs: number) {
  const store = new Map<string, CacheEntry<T>>();
  return {
    get(key: string): T | undefined {
      const entry = store.get(key);
      if (!entry) return undefined;
      if (Date.now() > entry.expiresAt) {
        store.delete(key);
        return undefined;
      }
      return entry.value;
    },
    set(key: string, value: T): void {
      store.set(key, { value, expiresAt: Date.now() + ttlMs });
    },
    clear(): void {
      store.clear();
    },
  };
}

const _routeCache = makeTTLCache<RouteResult>(30 * 60 * 1000); // 30 min
const _geocodeCache = makeTTLCache<GeocodingResult[]>(10 * 60 * 1000); // 10 min
const _reverseCache = makeTTLCache<string | null>(15 * 60 * 1000); // 15 min

function _routeCacheKey(waypoints: RouteWaypoint[], mode: RoutingMode): string {
  return (
    mode +
    "|" +
    waypoints.map((w) => `${w.lat.toFixed(5)},${w.lng.toFixed(5)}`).join("|")
  );
}

/** Svuota tutta la cache (utile dopo logout o cambio utente) */
export function clearMapCache(): void {
  _routeCache.clear();
  _geocodeCache.clear();
  _reverseCache.clear();
}

// ─── RETRY CON EXPONENTIAL BACKOFF ───────────────────────────────────────────

async function withRetry<T>(
  fn: () => Promise<T>,
  maxRetries = 2,
  baseDelayMs = 1000,
): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;
      if (attempt < maxRetries) {
        await new Promise((r) =>
          setTimeout(r, baseDelayMs * Math.pow(2, attempt)),
        );
      }
    }
  }
  throw lastError;
}

// ─── DEBOUNCE HELPER ─────────────────────────────────────────────────────────
// Uso: const debouncedGeocode = debounce(geocodeAddress, 400)

export function debounce<T extends (...args: any[]) => any>(
  fn: T,
  delayMs: number,
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delayMs);
  };
}

// ─── TILE LAYERS / MAPPA / MARKER ─────────────────────────────────────────────
// Le funzioni Leaflet (createTileLayer, initMap, destroyMap, drawRoute,
// addMarker, removeMarker) sono state spostate in ./leafletMapService.ts:
// tenendo qui l'import di "leaflet" l'intera libreria finiva nel bundle
// iniziale (questo modulo è importato in modo eager da WeatherWidget e
// memoryPressure), anche quando nessuna schermata mappa veniva aperta.

// ─── ROUTING: OSRM (primario, free) + Geoapify (fallback) ────────────────────

const OSRM_BASE = "https://router.project-osrm.org/route/v1";

/** Mappa modalità RideConnect → profilo OSRM */
const OSRM_PROFILE: Record<RoutingMode, string> = {
  motorcycle: "driving", // OSRM non ha profilo moto nativo, usa driving
  drive: "driving",
  walk: "foot",
  bicycle: "cycling",
};

/** Mappa modalità RideConnect → mode Geoapify */
const GEOAPIFY_MODE: Record<RoutingMode, string> = {
  motorcycle: "motorcycle",
  drive: "drive",
  walk: "walk",
  bicycle: "bicycle",
};

async function _getRouteOSRM(waypoints: RouteWaypoint[]): Promise<RouteResult> {
  const coords = waypoints.map((w) => `${w.lng},${w.lat}`).join(";");

  // OSRM usa profilo "driving" anche per moto (il più accurato disponibile free)
  const url = `${OSRM_BASE}/driving/${coords}?overview=full&geometries=geojson&steps=false`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`OSRM HTTP ${res.status}`);

  const json = await res.json();
  if (json.code !== "Ok" || !json.routes?.[0]) {
    throw new Error("OSRM: nessun percorso trovato");
  }

  const route = json.routes[0];
  const rawCoords: [number, number][] = route.geometry?.coordinates ?? [];

  const coordinates: Coordinates[] = rawCoords.map(([lng, lat]) => ({
    lat,
    lng,
  }));

  const result: RouteResult = {
    coordinates,
    distanceKm: parseFloat((route.distance / 1000).toFixed(2)),
    durationMin: Math.round(route.duration / 60),
    rawGeojson: json,
    provider: "osrm",
  };

  // Calcola curve score lato client
  try {
    result.curveScore = calculateCurveScore(coordinates);
  } catch {
    /* curveScore è opzionale, ignora errori */
  }

  return result;
}

async function _getRouteGeoapify(
  waypoints: RouteWaypoint[],
  mode: RoutingMode,
): Promise<RouteResult> {
  const key = getGeoapifyKey();
  if (!key) {
    throw new MapServiceError(
      "Chiave API Geoapify non configurata (VITE_GEOAPIFY_KEY).",
      "NO_API_KEY",
    );
  }

  const wpStr = waypoints.map((w) => `${w.lat},${w.lng}`).join("|");
  const gMode = GEOAPIFY_MODE[mode];
  const url = `https://api.geoapify.com/v1/routing?waypoints=${encodeURIComponent(
    wpStr,
  )}&mode=${gMode}&type=short&units=metric&format=geojson&apiKey=${encodeURIComponent(key)}`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`Geoapify routing HTTP ${res.status}`);

  const geojson = await res.json();
  const feature = geojson?.features?.[0];
  if (!feature) throw new Error("Nessun percorso trovato da Geoapify.");

  const props = feature.properties;
  const rawCoords: [number, number, number?][] =
    feature.geometry?.coordinates ?? [];

  const coordinates: Coordinates[] = rawCoords.map(([lng, lat]) => ({
    lat,
    lng,
  }));

  const elevationProfile: number[] | undefined = rawCoords.some(
    (c) => c[2] !== undefined,
  )
    ? rawCoords.map((c) => c[2] ?? 0)
    : undefined;

  const result: RouteResult = {
    coordinates,
    distanceKm: parseFloat(((props?.distance ?? 0) / 1000).toFixed(2)),
    durationMin: Math.round((props?.time ?? 0) / 60),
    elevationProfile,
    rawGeojson: geojson,
    provider: "geoapify",
  };

  // Calcola curve score con profilo altimetrico se disponibile
  try {
    result.curveScore = calculateCurveScore(coordinates, elevationProfile);
  } catch {
    /* curveScore è opzionale */
  }

  return result;
}

/**
 * Calcola l'indice di tortuosità (Curvy Roads Score) 1-10.
 *
 * Algoritmo:
 * 1. Analizza angoli consecutivi tra waypoint (ogni 3 punti)
 * 2. Conta curve significative (angolo > 25°) per km
 * 3. Penalizza tratti rettilinei (> 400m con angolo < 5°)
 * 4. Bonus dislivello se disponibile profilo altimetrico
 *
 * @returns Punteggio 1-10, arrotondato a 1 decimale
 */
export function calculateCurveScore(
  coordinates: Coordinates[],
  elevationProfile?: number[],
): number {
  if (!coordinates || coordinates.length < 3) return 5; // Default: medio

  const MIN_CURVE_ANGLE_DEG = 25; // Angolo minimo per considerare "curva"
  const STRAIGHT_THRESHOLD_DEG = 5; // Sotto questo angolo → rettilineo
  const STRAIGHT_MAX_METERS = 400; // Rettilineo max prima di penalizzare
  const CURVES_PER_KM_TARGET = 4; // 4 curve/km = score 10 (ottimo moto)

  let totalDistanceM = 0;
  let curveCount = 0;
  let straightSegments = 0;
  let totalElevationGain = 0;

  // ─── Fase 1: Calcolo distanza e angoli ──────────────────────────────
  for (let i = 0; i < coordinates.length - 2; i++) {
    const a = coordinates[i];
    const b = coordinates[i + 1];
    const c = coordinates[i + 2];

    // Distanza tra i punti in metri
    const d1 = haversineMeters(a, b);
    const d2 = haversineMeters(b, c);
    totalDistanceM += d1;

    // Calcolo angolo di sterzata
    const angleDeg = computeTurnAngle(a, b, c);

    if (angleDeg >= MIN_CURVE_ANGLE_DEG) {
      curveCount++;
    } else if (angleDeg < STRAIGHT_THRESHOLD_DEG) {
      // Verifica se è un rettilineo lungo
      if (d1 > STRAIGHT_MAX_METERS || d2 > STRAIGHT_MAX_METERS) {
        straightSegments++;
      }
    }
  }

  // Ultimo segmento
  if (coordinates.length >= 2) {
    totalDistanceM += haversineMeters(
      coordinates[coordinates.length - 2],
      coordinates[coordinates.length - 1],
    );
  }

  const totalDistanceKm = totalDistanceM / 1000;
  if (totalDistanceKm < 0.1) return 5; // Troppo corto per valutare

  // ─── Fase 2: Calcolo curve per km ───────────────────────────────────
  const curvesPerKm = curveCount / totalDistanceKm;

  // ─── Fase 3: Bonus altimetrico ──────────────────────────────────────
  if (elevationProfile && elevationProfile.length > 1) {
    for (let i = 1; i < elevationProfile.length; i++) {
      const diff = elevationProfile[i] - elevationProfile[i - 1];
      if (diff > 0) totalElevationGain += diff;
    }
  }

  // ─── Fase 4: Scoring ────────────────────────────────────────────────
  // Base: proporzione curve/km rispetto al target (max 10)
  let score = Math.min(10, (curvesPerKm / CURVES_PER_KM_TARGET) * 8);

  // Penalità rettilinei: -1 per ogni rettilineo oltre i primi 2
  if (straightSegments > 2) {
    score -= Math.min(3, (straightSegments - 2) * 0.5);
  }

  // Bonus altimetrico: +0.5 ogni 100m di dislivello (max +2)
  if (totalElevationGain > 0) {
    const elevBonus = Math.min(2, (totalElevationGain / 100) * 0.5);
    score += elevBonus;
  }

  // Bonus per percorsi molto tortuosi (> 6 curve/km)
  if (curvesPerKm > 6) {
    score += 1;
  }

  // Clamp 1-10
  return Math.max(1, Math.min(10, Math.round(score * 10) / 10));
}

/**
 * Versione in metri della distanza Haversine (wrapper della esistente in km).
 */
function haversineMeters(a: Coordinates, b: Coordinates): number {
  return haversineDistance(a, b) * 1000;
}

/**
 * Calcola l'angolo di sterzata tra tre punti consecutivi (in gradi).
 * Usa la legge del coseno sul triangolo formato dai 3 punti.
 */
function computeTurnAngle(
  a: Coordinates,
  b: Coordinates,
  c: Coordinates,
): number {
  const ab = haversineMeters(a, b);
  const bc = haversineMeters(b, c);
  const ac = haversineMeters(a, c);

  if (ab < 1 || bc < 1 || ac < 1) return 0; // Punti troppo vicini

  // Legge del coseno: cos(C) = (ab² + bc² - ac²) / (2 * ab * bc)
  let cosAngle = (ab * ab + bc * bc - ac * ac) / (2 * ab * bc);
  cosAngle = Math.max(-1, Math.min(1, cosAngle)); // Clamp per floating point

  const angleRad = Math.acos(cosAngle);
  const angleDeg = (angleRad * 180) / Math.PI;

  // L'angolo di sterzata è 180° - angolo interno
  return 180 - angleDeg;
}

/**
 * Calcola il percorso tra 2+ waypoint.
 * Strategia: OSRM (free, no key) → fallback Geoapify se OSRM fallisce.
 * Default mode: "motorcycle" — ottimale per biker.
 * Risultati cachati con TTL 30 minuti.
 * Aggiunge automaticamente il curveScore al risultato.
 */
export async function getRoute(
  waypoints: RouteWaypoint[],
  mode: RoutingMode = "motorcycle",
): Promise<RouteResult> {
  if (!waypoints || waypoints.length < 2) {
    throw new MapServiceError(
      "Sono necessari almeno 2 waypoint per calcolare un percorso.",
      "ROUTE_FETCH_FAILED",
    );
  }

  const cacheKey = _routeCacheKey(waypoints, mode);
  const cached = _routeCache.get(cacheKey);
  if (cached) return cached;

  // Tentativo 1: OSRM (con retry backoff)
  try {
    const result = await withRetry(() => _getRouteOSRM(waypoints));
    _routeCache.set(cacheKey, result);
    return result;
  } catch (osrmErr) {
    console.warn("[mapService] OSRM fallito, provo Geoapify:", osrmErr);
  }

  // Tentativo 2: Geoapify fallback (con retry backoff)
  try {
    const result = await withRetry(() => _getRouteGeoapify(waypoints, mode));
    _routeCache.set(cacheKey, result);
    return result;
  } catch (geoErr) {
    console.error("[mapService] getRoute: entrambi i provider falliti", geoErr);
    throw new MapServiceError(
      "Impossibile calcolare il percorso. Controlla la connessione e riprova.",
      "ROUTE_FETCH_FAILED",
    );
  }
}

// ─── GEOCODING: Nominatim/OSM (primario) + Geoapify (fallback) ───────────────

const NOMINATIM_BASE = "https://nominatim.openstreetmap.org";

async function _geocodeNominatim(
  query: string,
  limit: number,
): Promise<GeocodingResult[]> {
  const url = `${NOMINATIM_BASE}/search?format=json&addressdetails=1&limit=${limit}&q=${encodeURIComponent(
    query,
  )}&accept-language=it`;

  const res = await fetch(url, {
    headers: { "User-Agent": "RideConnect/2.0 (rideconnect.it)" },
  });
  if (!res.ok) throw new Error(`Nominatim HTTP ${res.status}`);

  const json: any[] = await res.json();
  if (!Array.isArray(json) || json.length === 0) return [];

  return json.map((item) => ({
    label: String(item.display_name || "").trim(),
    coordinates: {
      lat: parseFloat(item.lat),
      lng: parseFloat(item.lon),
    },
    details: {
      street: item.address?.road,
      city: item.address?.city || item.address?.town || item.address?.village,
      country: item.address?.country,
      postcode: item.address?.postcode,
    },
  }));
}

async function _geocodeGeoapify(
  query: string,
  limit: number,
): Promise<GeocodingResult[]> {
  const key = getGeoapifyKey();
  if (!key)
    throw new MapServiceError("Chiave API Geoapify mancante.", "NO_API_KEY");

  const url = `https://api.geoapify.com/v1/geocode/search?text=${encodeURIComponent(
    query.trim(),
  )}&limit=${limit}&lang=it&apiKey=${encodeURIComponent(key)}`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`Geoapify geocode HTTP ${res.status}`);

  const json = await res.json();
  const features = Array.isArray(json?.features) ? json.features : [];

  return features.map((f: any) => {
    const p = f?.properties ?? {};
    return {
      label: String(p.formatted || p.name || "").trim(),
      coordinates: {
        lat: f.geometry?.coordinates?.[1] ?? 0,
        lng: f.geometry?.coordinates?.[0] ?? 0,
      },
      details: {
        street: p.street,
        city: p.city,
        country: p.country,
        postcode: p.postcode,
      },
    };
  });
}

/**
 * Forward geocoding: testo → coordinate.
 * Strategia: Nominatim (free, no key) → fallback Geoapify.
 * Cache TTL 10 minuti. Usa debounce(geocodeAddress, 400) nei componenti!
 *
 * @param query — testo libero (es. "Via Emilia 10, Modena")
 * @param limit — max risultati (default 5)
 */
export async function geocodeAddress(
  query: string,
  limit = 5,
): Promise<GeocodingResult[]> {
  if (!query?.trim()) return [];

  const cacheKey = `${query.trim().toLowerCase()}__${limit}`;
  const cached = _geocodeCache.get(cacheKey);
  if (cached) return cached;

  // Tentativo 1: Nominatim (con retry)
  try {
    const results = await withRetry(() =>
      _geocodeNominatim(query.trim(), limit),
    );
    if (results.length > 0) {
      _geocodeCache.set(cacheKey, results);
      return results;
    }
  } catch (err) {
    console.warn("[mapService] Nominatim fallito, provo Geoapify:", err);
  }

  // Tentativo 2: Geoapify fallback
  try {
    const results = await withRetry(() =>
      _geocodeGeoapify(query.trim(), limit),
    );
    _geocodeCache.set(cacheKey, results);
    return results;
  } catch (err) {
    console.error(
      "[mapService] geocodeAddress: entrambi i provider falliti",
      err,
    );
    throw new MapServiceError(
      "Ricerca indirizzo fallita. Prova con un termine più specifico.",
      "GEOCODE_FAILED",
    );
  }
}

/**
 * Reverse geocoding: coordinate → indirizzo testuale.
 * Usa Nominatim prima, Geoapify come fallback. Cache TTL 15 minuti.
 *
 * FIX "Y1 / T2 / T3" (Ott 2026): Nominatim viene interrogato con
 * `format=jsonv2&addressdetails=1` e il risultato passa da `buildGeoLabel`
 * (shared/geoLabel.ts), che scarta i nomi-codice OSM (T2, Y1, T11…) e le
 * feature non pertinenti (ascensori, scalinate, fermate bus) preferendo
 * strada → nome → località. Ritorna `null` se non c'è nulla di utile, così i
 * chiamanti possono applicare il proprio fallback ("Punto di ritrovo").
 */
export async function reverseGeocode(
  lat: number,
  lng: number,
): Promise<string | null> {
  const cacheKey = `${lat.toFixed(5)},${lng.toFixed(5)}`;
  const cached = _reverseCache.get(cacheKey);
  if (cached !== undefined) return cached;

  // Tentativo 1: Nominatim reverse (jsonv2 + indirizzo strutturato)
  try {
    const url = `${NOMINATIM_BASE}/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=16&addressdetails=1&accept-language=it`;
    const res = await fetch(url, {
      headers: { "User-Agent": "RideConnect/3.5.0 (rideconnect.it)" },
    });
    if (res.ok) {
      const json: any = await res.json();
      const placeName = String(json?.name ?? "").trim();
      const result = buildGeoLabel({
        displayName: json?.display_name ?? null,
        featureClass: json?.class ?? json?.category ?? null,
        featureType: json?.type ?? null,
        address: json?.address ?? null,
        placeName: placeName || null,
      });
      if (!result.usedFallback) {
        _reverseCache.set(cacheKey, result.full);
        return result.full;
      }
    }
  } catch {
    // continua con fallback
  }

  // Tentativo 2: Geoapify reverse (campi strutturati: name/street/address_line)
  const key = getGeoapifyKey();
  if (!key) {
    _reverseCache.set(cacheKey, null);
    return null;
  }

  try {
    const url = `https://api.geoapify.com/v1/geocode/reverse?lat=${lat}&lon=${lng}&lang=it&limit=1&apiKey=${encodeURIComponent(key)}`;
    const res = await fetch(url);
    const json = await res.json();
    const f = Array.isArray(json?.features) ? json.features[0] : null;
    const props = f?.properties ?? {};
    // `formatted` di Geoapify include i codici solo come suffisso: il nome
    // human-readable sta in `name`/`street`/`address_line1`.
    const result = buildGeoLabel({
      displayName: props?.formatted ?? props?.address_line1 ?? null,
      placeName: props?.name ?? props?.street ?? null,
      featureClass: props?.category ?? props?.result_type ?? null,
      featureType: props?.result_type ?? null,
      address: {
        road: props?.street ?? null,
        house_number: props?.housenumber ?? null,
        village: props?.village ?? null,
        hamlet: props?.hamlet ?? null,
        town: props?.town ?? null,
        city: props?.city ?? null,
        suburb: props?.suburb ?? null,
        county: props?.county ?? null,
        state: props?.state ?? null,
      },
    });
    const label = result.usedFallback ? null : result.full;
    _reverseCache.set(cacheKey, label);
    return label;
  } catch {
    return null;
  }
}

// ─── GEOLOCALIZZAZIONE ────────────────────────────────────────────────────────

export function getCurrentPosition(timeout = 10000): Promise<Coordinates> {
  return new Promise((resolve, reject) => {
    if (!navigator?.geolocation) {
      return reject(
        new MapServiceError(
          "Il tuo dispositivo non supporta la geolocalizzazione.",
          "GEOLOCATION_UNSUPPORTED",
        ),
      );
    }

    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        }),
      (err) => {
        const map: Record<
          number,
          { msg: string; code: MapServiceError["code"] }
        > = {
          1: {
            msg: "Permesso di geolocalizzazione negato. Abilitalo nelle impostazioni del browser.",
            code: "GEOLOCATION_DENIED",
          },
          2: {
            msg: "Posizione non disponibile. Riprova tra qualche secondo.",
            code: "GEOLOCATION_ERROR",
          },
          3: {
            msg: "Timeout nella rilevazione GPS. Controlla la connessione.",
            code: "GEOLOCATION_TIMEOUT",
          },
        };
        const e = map[err.code] ?? {
          msg: "Errore sconosciuto nella geolocalizzazione.",
          code: "GEOLOCATION_ERROR" as const,
        };
        reject(new MapServiceError(e.msg, e.code));
      },
      { timeout, enableHighAccuracy: true, maximumAge: 30_000 },
    );
  });
}

// ─── UTILITÀ ─────────────────────────────────────────────────────────────────

export function haversineDistance(a: Coordinates, b: Coordinates): number {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const h =
    sinDLat * sinDLat +
    Math.cos((a.lat * Math.PI) / 180) *
      Math.cos((b.lat * Math.PI) / 180) *
      sinDLng *
      sinDLng;
  return parseFloat((R * 2 * Math.asin(Math.sqrt(h))).toFixed(2));
}

export function formatDuration(minutes: number): string {
  if (!minutes || minutes <= 0) return "—";
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

export function formatDistance(km: number): string {
  if (!km || km <= 0) return "—";
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toLocaleString("it-IT", { maximumFractionDigits: 1 })} km`;
}

// ─── RE-EXPORT compatibilità con geoapify.ts (da rimuovere dopo migrazione) ──

/** @deprecated Usa getRoute() da mapService.ts */
export async function fetchRoutingGeojson(
  waypoints: RouteWaypoint[],
): Promise<unknown | null> {
  try {
    const result = await getRoute(waypoints);
    // FIX MAPPA-DRITTA (Set 2026): rawGeojson di OSRM è l'envelope
    // { code, routes: [...] } — NON è GeoJSON. Costruiamo SEMPRE una
    // FeatureCollection canonica dalle coordinate calcolate, così il
    // risultato è usabile sia dal parser polyline sia dalle tappe.
    return {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          properties: { provider: result.provider },
          geometry: {
            type: "LineString",
            coordinates: result.coordinates.map((c) => [c.lng, c.lat]),
          },
        },
      ],
    };
  } catch {
    return null;
  }
}
